import { SdkContext } from '../internal'
import type { MemberWelcome, MissionsCard, WelcomeSettings, WelcomeSettingsInput } from '@/types/welcome'

/** What `POST /api/welcome/progress` takes for the current screen. */
export interface WelcomeProgressInput {
  /** The screen being finished (0-based); one out of order is 409, a resent one answers the state. */
  step: number
  rulesAccepted?: boolean
  videoFinished?: boolean
  /** The screen's fields by key. */
  fields?: Record<string, unknown>
}

/**
 * F-155 + F-190: the community's welcome. Settings are owner and admin; the
 * rest is the person's own (the team is never pending, and `exempt` from the
 * missions).
 */
export class WelcomeResource {
  constructor(private ctx: SdkContext) {}

  async settings(): Promise<WelcomeSettings> {
    return this.ctx.call({ httpMethod: 'GET', path: '/welcome/settings' })
  }

  async saveSettings(body: WelcomeSettingsInput): Promise<WelcomeSettings> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/welcome/settings', hasBody: true }, { body })
  }

  async mine(): Promise<MemberWelcome> {
    return this.ctx.call({ httpMethod: 'GET', path: '/welcome' })
  }

  async progress(body: WelcomeProgressInput): Promise<MemberWelcome> {
    return this.ctx.call({ httpMethod: 'POST', path: '/welcome/progress', hasBody: true }, { body })
  }

  /** Only when the welcome is skippable (409 `welcome.notSkippable` otherwise). */
  async skip(): Promise<MemberWelcome> {
    return this.ctx.call({ httpMethod: 'POST', path: '/welcome/skip', hasBody: true }, { body: {} })
  }

  async missions(): Promise<MissionsCard> {
    return this.ctx.call({ httpMethod: 'GET', path: '/welcome/missions' })
  }

  /** Hides the card for good: nothing counts after. */
  async dismissMissions(): Promise<MissionsCard> {
    return this.ctx.call({ httpMethod: 'POST', path: '/welcome/missions/dismiss', hasBody: true }, { body: {} })
  }

  /** The congratulations were shown: not again. */
  async missionsCelebrated(): Promise<MissionsCard> {
    return this.ctx.call({ httpMethod: 'POST', path: '/welcome/missions/celebrated', hasBody: true }, { body: {} })
  }
}
